/**
 * src/lib/auditState.ts
 * React adaptation of the original HTML report state engine.
 * Preserves exact STATE_KEY so HTML report state is importable here.
 */
import type { StateMap, FixStatus } from "../types";

export const STATE_KEY = "seo_v5_state";

export function loadState(): StateMap {
  try { return JSON.parse(localStorage.getItem(STATE_KEY) ?? "{}"); }
  catch { return {}; }
}
export function saveState(s: StateMap): void {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch {}
}
export function setApproved(s: StateMap, id: string): StateMap {
  if (s[id] === "done") return s;
  return { ...s, [id]: "queued" as FixStatus };
}
export function setIgnored(s: StateMap, id: string, ignore: boolean): StateMap {
  if (s[id] === "done") return s;
  if (ignore) return { ...s, [id]: "ignored" as FixStatus };
  const next = { ...s };
  if (next[id] === "ignored") delete next[id];
  return next;
}
export function setDone(s: StateMap, id: string, done: boolean): StateMap {
  if (done) return { ...s, [id]: "done" as FixStatus };
  const next = { ...s };
  if (next[id] === "done") delete next[id];
  return next;
}
// Removes any status for a fix (queued / done / ignored) so it returns to the
// un-actioned state — used by the reset icon to clear a hung "queued" status.
export function clearState(s: StateMap, id: string): StateMap {
  if (!(id in s)) return s;
  const next = { ...s };
  delete next[id];
  return next;
}
export function getCounts(s: StateMap) {
  const vals = Object.values(s);
  return {
    done:    vals.filter(v => v === "done").length,
    queued:  vals.filter(v => v === "queued").length,
    ignored: vals.filter(v => v === "ignored").length,
  };
}
